import { ZuiTableBaseElement } from './base.js';
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-table-cell-css.js';

/**
 * An individual table cell to be passed into a `<zui-table-row>`.
 *
 * @element zui-table-cell
 *
 * @slot - Default, unnamed slot; for inserting table cell content into `<zui-table-cell>`
 *
 * @cssprop [--zui-table-cell-padding=13px 20px] - Override cell padding
 * @cssprop [--zui-table-cell-sort-color=var(--zui-gray-300)] - ([sortable]): Default color of chevron when `direction` is `null`
 * @cssprop [--zui-table-cell-sort-active-color=var(--zui-blue)] - Highlight color used to indicate the active sort direction
 *
 */
export class ZuiTableCellElement extends ZuiTableBaseElement {
  /**
   * Set to decrease table cell padding to accommodate action button(s)
   */
  @property({ type: Boolean, reflect: true })
  action = false;

  /**
   * Current sort direction of the sortable cell
   * @type {null | "ascending" | "descending"}
   */
  @property({ type: String, reflect: true })
  get sort(): ZuiTableSortDirection {
    return this.#sort;
  }

  set sort(val: ZuiTableSortDirection) {
    const acceptableVals = ['ascending', 'descending', null];
    if (acceptableVals.includes(val)) {
      const oldVal = this.#sort;
      this.#sort = val;
      this.requestUpdate('sort', oldVal);
      this._state?.channel.dispatchEvent(new CustomEvent('sort', { detail: { sort: val, cell: this } }));
    }
  }

  /**
   * (`zui-table-row[header]`): Whether or not cell header is sortable; another requirement is the parent element, `<zui-table-cell header>` must have `header` attribute or property set
   */
  @property({ type: Boolean, reflect: true })
  get sortable() {
    return this._isAllowedSort && this.#sortable;
  }

  set sortable(val: boolean) {
    const oldVal = this.#sortable;
    this.#sortable = val;
    this.requestUpdate('sortable', oldVal);
  }

  /**
   * This private field is needed to persist state between the cell and the header row. DO NOT USE EXTERNALLY
   */
  @state()
  private _isAllowedSort = false;

  @state()
  private _header?: string;

  #sort: ZuiTableSortDirection = null;
  #sortable = false;

  static get styles() {
    return [super.styles, style];
  }

  render() {
    if (this.sortable) {
      import('@zywave/zui-icons');
    }
    const styles = { 'is-selectable': this.sortable };
    return html`<div class="wrapper"
      ><div class="mobile-header">${this._header ?? ''}</div
      ><div @click="${this.#onSortableClick}" class="${classMap(styles)}">
        <slot></slot>
        ${this.sortable ? html`<zui-icon icon="zui-sort"></zui-icon>` : nothing}
      </div></div
    >`;
  }

  /**
   * Handles click event on sortable header cells
   */
  click() {
    super.click();

    this.#onSortableClick();
  }

  #onSortableClick() {
    if (this.sortable) {
      switch (this.sort) {
        case 'ascending':
          this.sort = 'descending';
          break;
        case 'descending':
          this.sort = null;
          break;
        case null:
        default:
          this.sort = 'ascending';
          break;
      }
    }
  }
}

window.customElements.define('zui-table-cell', ZuiTableCellElement);

export type ZuiTableSortDirection = null | 'ascending' | 'descending';

declare global {
  interface HTMLElementTagNameMap {
    'zui-table-cell': ZuiTableCellElement;
  }
}
